import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AisRestService, BaseQuestionIdentifier, CaseResource, EmbeddableResourceNames, InterviewResourceIdentifier, LifeResourceIdentifier, ListDefinitionResource, LocationToIdentifierHelper, QuestionPageResource, QuestionPagesResource, QuestionSectionResource, QuestionType, ScreensQuestionResource } from '@Components/mras/core';
import { NavigationFromInterviewData, ThirdPartyNavigationDirection, ThirdPartySectionService } from '@Components/third-party-section/third-party-section.service';
import { InterviewService } from '@App/interview.service';
import { NotificationService, NotificationSeverity } from '@Services';
import { Subscription } from 'rxjs';

@Component({
    selector: 'mras-interview',
    templateUrl: './screens-view.component.html',
    styleUrls: ['screens-view.component.scss']
})
export class ScreensViewComponent implements OnInit, OnChanges, OnDestroy {
    readonly EmbeddableResourceNames = EmbeddableResourceNames;
    readonly QuestionType = QuestionType;
    questionPagesResourceIdentifier: InterviewResourceIdentifier;
    questionPagesResource: QuestionPagesResource;
    selectedInterviewPage: QuestionPageResource;
    lifeIdentifier: LifeResourceIdentifier;
    countries: ListDefinitionResource;
    caseDetails = new CaseResource();

    subscriptions: Subscription[] = [];
    sections: QuestionSectionResource[];
    bqToSection = new Map<string, ScreensQuestionResource[]>();

    thirdPartySectionSubmittedSubscription: Subscription;    
    
    _caseId: string; 
    get caseId(): string {
        return this._caseId;
    }

    @Input() 
    set caseId(value: string) {
        this._caseId = value;
    }
    @Input() interviewId: string;
    @Input() activeEnvironment: string;
    @Input() summaryMode: boolean;
    
    _pageIndex;
    get pageIndex(): number {
        return this._pageIndex;
    }
    
    @Input() 
    set pageIndex(value: number) {
        this._pageIndex = value;
    }


    constructor(public aisRestService: AisRestService, 
        public locationToIdentifier: LocationToIdentifierHelper, 
        public thirdPartySectionService: ThirdPartySectionService, 
        public interviewService: InterviewService, 
        public notificationService: NotificationService) {        
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    ngOnInit() {
        this.summaryMode = this.interviewService.isReadOnly;
        this.aisRestService.activeEnvironment = this.activeEnvironment;
        // this._caseId = caseId;
        // this.interviewId = interviewId;
        // this._pageIndex = pageIndex;

        this.questionPagesResourceIdentifier = {caseId: this._caseId, interviewCode: this.interviewId,  interviewInstance: "1", lifeInstance: "1", languageCode: "en"};
        this.lifeIdentifier = {caseId: this._caseId, languageCode: "en", lifeInstance: "1"};
        
        this.locationToIdentifier.identifier = this.questionPagesResourceIdentifier;
        this.locationToIdentifier.lifeResourceIdentifier = this.lifeIdentifier;

        this.aisRestService.getBaseQuestionsInHierarchy(this.questionPagesResourceIdentifier)
            .subscribe(res => {
                res._embedded = res._embedded || ({} as [EmbeddableResourceNames, any[]]);
                const embedded = res._embedded;
                embedded[EmbeddableResourceNames.DISCLOSURES] = embedded[EmbeddableResourceNames.DISCLOSURES] || [];
                embedded[EmbeddableResourceNames.DISCLOSURE_PHYSICIANS] = embedded[EmbeddableResourceNames.DISCLOSURE_PHYSICIANS] || [];
                embedded[EmbeddableResourceNames.LISTS] = embedded[EmbeddableResourceNames.LISTS] || [];
                embedded[EmbeddableResourceNames.AFFECTED_QUESTIONS] = embedded[EmbeddableResourceNames.AFFECTED_QUESTIONS] || [];
                embedded[EmbeddableResourceNames.PHYSICIANS] = embedded[EmbeddableResourceNames.PHYSICIANS] || [];

                this.questionPagesResource = res;
                this.selectedInterviewPage = res.pages[this._pageIndex];

                this.countries = embedded[EmbeddableResourceNames.LISTS].find(e => e.listName === 'Countries');
                this.updateQuestions();
            });
        
        this.aisRestService.getCase(this.questionPagesResourceIdentifier)
            .subscribe(res => {
                this.caseDetails = res;
            });

        if (!this.thirdPartySectionSubmittedSubscription) {
            this.thirdPartySectionSubmittedSubscription = this.thirdPartySectionService.navigationFromInterviewObservable.subscribe(
                (data: NavigationFromInterviewData) => {

                    //validate form
                    if (data.userClickedNext) {
                        let isInvalid = false;
                        let invalidQuestions = [];
                        let invalidDisclosures = [];
                        const invalidBaseQuestions = [];

                        const page = this.questionPagesResource.pages[this._pageIndex];

                        page?.sections?.forEach(section => {
                            invalidQuestions = section.baseQuestions.filter(x => 
                                x.visible 
                                && ((!x.answerValue || x.answerValue === null || x.answerValue === '')
                                    || (x.answerType === "PICKLIST" && x.answerValue !== 'false' && (!x.disclosures || x.disclosures.length <=0))
                                ));

                            invalidQuestions.forEach(x => x.invalid = true);

                            if (invalidQuestions.length > 0) {
                                isInvalid = true;
                            }

                            this.questionPagesResource._embedded[EmbeddableResourceNames.DISCLOSURES].filter(disclosure =>  {
                                let disclosureOnPage = false;

                                if (!disclosure.removableFromBqCodes) {
                                    return;
                                }
                                let baseQuestion;
                                const baseQuestionDisclosureCode = disclosure.removableFromBqCodes[0];
                                this.sections.forEach(section => {
                                    baseQuestion = section.baseQuestions.find(y => y.code === baseQuestionDisclosureCode);
                                    if (baseQuestion) {
                                        disclosureOnPage = true;
                                    }
                                });

                                if (!disclosureOnPage) {
                                    return;
                                }

                                const d = disclosure.questions?.filter(x => !x.answerValue || x.answerValue === null || x.answerValue === '');

                                if (d && d.length > 0) {
                                    invalidDisclosures = invalidDisclosures.concat(d);
                                    invalidBaseQuestions.push(baseQuestion);
                                }
                            });

                            invalidDisclosures?.forEach(x => x.invalid = true);

                            if (invalidDisclosures?.length > 0) {
                                isInvalid = true;
                            }
       
                        });

                        if (isInvalid) {
                            this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: 'Check error message(s)' });

                            if (invalidQuestions.length > 0){
                                this.scrollToTarget(invalidQuestions[0].code);

                                return;
                            }

                            if (invalidDisclosures.length > 0 && invalidDisclosures[0].code){
                                this.scrollToTarget(invalidDisclosures[0].code);

                                return;
                            }

                            if (invalidBaseQuestions.length > 0){
                                this.scrollToTarget(invalidBaseQuestions[0].code);

                                return;
                            }
                            return;
                        }

                             

                        if (!isInvalid) {
                            this.thirdPartySectionService.thirdPartySectionNavigation(ThirdPartyNavigationDirection.Next);
                        }
                    }
                     
                    if (data.userClickedPrevious) this.thirdPartySectionService.thirdPartySectionNavigation(ThirdPartyNavigationDirection.Previous);    
                });
                
            this.subscriptions.push(this.thirdPartySectionSubmittedSubscription);
        }
    }

    scrollToTarget(target): void {
        const element = document.getElementById(target);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    questionIdentifier(question: ScreensQuestionResource): BaseQuestionIdentifier {
        const questionIdentifier = new BaseQuestionIdentifier();
        questionIdentifier.caseId = this.questionPagesResourceIdentifier.caseId;
        questionIdentifier.languageCode = this.questionPagesResourceIdentifier.languageCode;
        questionIdentifier.interviewCode = this.questionPagesResourceIdentifier.interviewCode;
        questionIdentifier.lifeInstance = this.questionPagesResourceIdentifier.lifeInstance;
        questionIdentifier.interviewInstance = this.questionPagesResourceIdentifier.interviewInstance;
        questionIdentifier.baseQuestionCode = question.code;
        return questionIdentifier;
    }
    
    changeInterviewPage() {
        this.updateQuestions();
    }

    handleQuestionChange(changed: ScreensQuestionResource) {
        this.questionPagesResource.pages.forEach(page => {
            page.sections.forEach(section => {
                section.baseQuestions.forEach(question => {
                    if (question.code !== changed.code) {
                        return;
                    }
                    Object.keys(question).forEach(k => delete question[k]);
                    Object.assign(question, changed);
                    this.updateQuestions();
                });
            });
        });
    }

    updateQuestions() {
        this.bqToSection.clear();

        const sections = this.selectedInterviewPage?.sections;

        if (sections) {
            this.sections = sections;
            for (const section of this.sections) {
                this.bqToSection.set(section.code, section.baseQuestions.filter(e => e.visible === true));
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.updateQuestions();
    }
}
